import React, { useEffect, useState } from 'react';
import Page from '@Containers/Page';
import { newToastMessage } from '@Containers/ToastMessages/ToastMessagesActions';
import { CIBuildNumber, CIBuildDate, CIBuildFeatures, betaConfig } from '@Constants/CommonConstants';
import RequestUrls from '@Constants/RequestUrlsConstants';
import store from '@Store';
import MessageBroker from '@Services/MessageBroker';
import AppStorage from '@Services/AppStorage';
import Catalyst from '@Services/Catalyst';
import Auth from '@Services/Auth';
import { reloadLocation, getLocationHostname } from '@Utils/navigation';
import cloneDeep from 'lodash/cloneDeep';
import isString from 'lodash/isString';
import each from 'lodash/each';
import reject from 'lodash/reject';
import defaults from 'lodash/defaults';
import keys from 'lodash/keys';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import RouteChangeTracking from '@Services/RouteChangeTracking';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';

import Dev from './Dev';

function DevContainer() {
  const [devDetails, setDevDetails] = useState({});
  const [healthCheck, setHealthCheck] = useState('');
  const [beBuildDetails, setBeBuildDetails] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const combinedRouteParams = getCombinedRouteParams(store.getState()) || {};
  useEffect(() => {
    // eslint-disable-next-line no-use-before-define
    initializeDev();
    RouteChangeTracking.trackOnRouteChangeSuccess();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isInitialized) {
      // eslint-disable-next-line no-use-before-define
      modifyDev();
    }
    // eslint-disable-next-line
  }, [isInitialized]);

  const killMySession = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('sure?')) {
      MessageBroker.send(RequestUrls.userLogout);
      // eslint-disable-next-line no-alert
      window.alert("you're all set");
    }
  };
  const mockSessionTimeout = () => {
    killMySession();
    Auth.updateUser({ details: { sessionTimeout: 1 } });
    Auth.setLastActionTime();
  };

  const isProxy = () => {
    const whitelist = [/(^|\.)reaxys\.com$/, /(^|\.)cm-elsevier\.com$/];
    return !whitelist.some((regExp) => regExp.test(getLocationHostname()));
  };

  const createTrueMap = (args) => {
    const map = {};
    if (isString(args)) {
      each(
        reject(args.split(','), (item) => item === 'isWebsocketsOverCDN'),
        (item) => {
          const [key, value] = item.split('=');
          map[key] = value || true;
        }
      );
    }
    return map;
  };

  const syncWSParams = (newValue, override) => {
    const updatedBEURL = cloneDeep(devDetails.BEURL, !isProxy());
    if (!isProxy()) {
      if (newValue) {
        updatedBEURL.WSUrlPrefix = devDetails.BEURL.RestUrlPrefix;
      } else {
        updatedBEURL.WSUrlPrefix = window.BUILD.getENVConfig().WSUrlPrefix;
      }
      if (override) {
        AppStorage.instance.set('BEURL', updatedBEURL);
      }
      setDevDetails({ ...devDetails, BEURL: { ...cloneDeep(updatedBEURL), isWebsocketsOverCDN: newValue } });
    }
  };

  const saveBEURL = (isReset) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('sure?')) {
      if (isReset) {
        AppStorage.instance.remove('BEURL');
      } else {
        AppStorage.instance.set('BEURL', devDetails.BEURL);
      }
      const isWebsocketsOverCDN = devDetails.BEURL && devDetails.BEURL.WSUrlPrefix === devDetails.BEURL.RestUrlPrefix;
      setDevDetails({
        ...devDetails,
        BEURL: {
          ...cloneDeep(devDetails.BEURL),
          isWebsocketsOverCDN,
        },
      });
      reloadLocation();
    }
  };

  const updateDev = (param, value) => {
    const isEqualParams = isEqual(devDetails[param], value);
    if (isEqualParams) return;
    setDevDetails({ ...devDetails, [param]: cloneDeep(value) });
    if (param === 'options' && !isEqualParams) {
      Auth.updateUser({ devOptions: cloneDeep(value) });
    }
  };

  const initializeDev = async () => {
    const options = defaults(createTrueMap(combinedRouteParams.features), get(Auth.getUser(), 'devOptions', {}));
    if (combinedRouteParams.features && combinedRouteParams.features.indexOf('resetDefaults') !== -1) {
      AppStorage.instance.remove('BEURL');
      Auth.cleanUp();
    }
    const BEURL = AppStorage.instance.get('BEURL') || (get(window, 'BUILD.getENVConfig') && window.BUILD.getENVConfig());
    const BEURLList = keys(BEURL);
    const config = betaConfig;
    const FEBuildNumber = CIBuildNumber;
    const FEBuildFeatures = CIBuildFeatures;
    const FEBuildDate = CIBuildDate;
    setDevDetails({
      options,
      BEURL,
      BEURLList,
      config,
      FEBuildNumber,
      FEBuildFeatures,
      FEBuildDate,
      isProxy: isProxy(),
    });
    setIsInitialized(true);
  };

  const modifyDev = async () => {
    let healthCheckVal;
    let BEBuildNumber;
    let BEBuildDate;
    if (combinedRouteParams.features) {
      if (combinedRouteParams.features.indexOf('isWebsocketsOverCDN') >= 0 && !isProxy()) {
        syncWSParams(true, true);
      }
      Auth.updateUser({ devOptions: devDetails.options });
      Auth.goToLandingPage();
      newToastMessage({
        title: 'Features enabled:',
        content: combinedRouteParams.features.split(',').join('\n'),
      });
    }
    if (Auth.getAccessToken()) {
      MessageBroker.send(RequestUrls.healthCheck)
        .then((response) => {
          healthCheckVal = JSON.stringify(response, undefined, 2);
          setHealthCheck(healthCheckVal);
        })
        .catch(Logger.error);
    } else {
      const errorMessage = 'Your premium subscription to our services has expired. Please login to renew it.';
      Catalyst.trackError({
        message: errorMessage,
        component: 'Health Check',
      });
      healthCheckVal = errorMessage;
      setHealthCheck(healthCheckVal);
    }
    MessageBroker.send(RequestUrls.buildInfo)
      .then((response) => {
        BEBuildNumber = response.buildNumber;
        BEBuildDate = response.buildTimestamp;
        setBeBuildDetails({
          BEBuildNumber,
          BEBuildDate,
        });
      })
      .catch(Logger.error);
  };
  return (
    <Page headerType="main" withFooter pageClassName="dev-page" className="react-page-static__wrapper">
      <main id="main-content" className="page-container page-container--hexagons dev-container" aria-label="Dev">
        <Dev
          devDetails={{ ...devDetails, healthCheck, ...beBuildDetails }}
          healthCheck={healthCheck}
          updateDev={updateDev}
          syncWSParams={syncWSParams}
          killMySession={killMySession}
          mockSessionTimeout={mockSessionTimeout}
          saveBEURL={saveBEURL}
        />
      </main>
    </Page>
  );
}

export default DevContainer;
